import React, { useState } from "react";
import { T } from "./constants";

// ── MultilineText — renders \n as actual line breaks everywhere ──
export function MultilineText({ text, style = {} }) {
  if (!text) return null;
  return (
    <div style={style}>
      {text.split('\n').map((line, i) => {
        if (line.trim() === '') return <div key={i} style={{ height: 6 }} />;
        const isArrow = line.trim().startsWith('\u2192') || line.trim().startsWith('\u2022');
        const isBold  = line.trim().endsWith(':') && line.length < 60;
        return (
          <p key={i} style={{
            margin: '0 0 3px',
            lineHeight: 1.65,
            paddingLeft: isArrow ? 8 : 0,
            fontWeight: isBold ? 700 : 'inherit',
            color: 'inherit',
            fontSize: 'inherit',
          }}>{line}</p>
        );
      })}
    </div>
  );
}

// ── LUMI MARKDOWN RENDERER ─────────────────────────────────────
export function renderInline(text) {
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  let last = 0, match, k = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(<span key={k++}>{text.slice(last, match.index)}</span>);
    const m = match[0];
    if (m.startsWith('**')) parts.push(<strong key={k++} style={{color:T.text,fontWeight:700}}>{m.slice(2,-2)}</strong>);
    else if (m.startsWith('*')) parts.push(<em key={k++}>{m.slice(1,-1)}</em>);
    else if (m.startsWith('`')) parts.push(<code key={k++} style={{background:T.surface,padding:'1px 5px',borderRadius:4,fontSize:12,fontFamily:'monospace',color:T.accent}}>{m.slice(1,-1)}</code>);
    last = match.index + m.length;
  }
  if (last < text.length) parts.push(<span key={k++}>{text.slice(last)}</span>);
  return parts.length ? parts : text;
}

export function LumiMarkdown({ text }) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let key = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.startsWith('### ')) {
      elements.push(<p key={key++} style={{fontWeight:800,fontSize:14,color:T.text,margin:'8px 0 2px'}}>{renderInline(line.slice(4))}</p>);
    } else if (line.startsWith('## ')) {
      elements.push(<p key={key++} style={{fontWeight:800,fontSize:15,color:T.text,margin:'8px 0 4px'}}>{renderInline(line.slice(3))}</p>);
    }
    else if (line.startsWith('- ') || line.startsWith('\u2022 ')) {
      elements.push(
        <div key={key++} style={{display:'flex',gap:6,margin:'2px 0'}}>
          <span style={{color:T.accent,fontWeight:700,flexShrink:0}}>{'\u2022'}</span>
          <span style={{fontSize:13,color:T.text,lineHeight:1.55}}>{renderInline(line.slice(2))}</span>
        </div>
      );
    }
    else if (/^\d+\.\s/.test(line)) {
      const num = line.match(/^(\d+)\./)[1];
      elements.push(
        <div key={key++} style={{display:'flex',gap:6,margin:'2px 0'}}>
          <span style={{color:T.accent,fontWeight:700,flexShrink:0,minWidth:16}}>{num}.</span>
          <span style={{fontSize:13,color:T.text,lineHeight:1.55}}>{renderInline(line.replace(/^\d+\.\s/,''))}</span>
        </div>
      );
    }
    else if (line.trim() === '') {
      elements.push(<div key={key++} style={{height:4}} />);
    }
    else {
      elements.push(<p key={key++} style={{fontSize:13,color:T.text,lineHeight:1.6,margin:'2px 0'}}>{renderInline(line)}</p>);
    }
  }
  return <div>{elements}</div>;
}

// ── SHARED UI COMPONENTS ───────────────────────────────────────
export function Btn({ children, onClick, variant="primary", size="md", disabled, color, full }) {
  const variants = {
    primary: { bg: T.accent, text: "#fff", border: T.accent },
    ghost:   { bg: "transparent", text: T.textMid, border: T.border },
    danger:  { bg: T.red, text: "#fff", border: T.red },
    gold:    { bg: T.gold, text: T.bg, border: T.gold },
  };
  const sizes = { sm: "8px 16px", md: "11px 22px", lg: "14px 28px" };
  const v = variants[variant];
  return (
    <button onClick={disabled ? undefined : onClick} style={{
      background: color ? color : v.bg,
      color: v.text, border: `1.5px solid ${color || v.border}`,
      borderRadius: 10, padding: sizes[size], fontSize: size === "sm" ? 13 : size === "lg" ? 16 : 14,
      fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
      fontFamily: "'Plus Jakarta Sans', sans-serif", opacity: disabled ? 0.4 : 1,
      transition: "all .15s", width: full ? "100%" : undefined,
      letterSpacing: "0.01em",
    }}
    onMouseOver={e => { if (!disabled) { e.currentTarget.style.opacity = "0.85"; e.currentTarget.style.transform = "translateY(-1px)"; }}}
    onMouseOut={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "translateY(0)"; }}
    >{children}</button>
  );
}

// ── ERROR BOUNDARY ────────────────────────────────────────────────
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error('Empirio ErrorBoundary caught:', error, info);
  }
  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        // Support render-prop pattern: fallback can be a function that receives the error
        if (typeof this.props.fallback === 'function') {
          return this.props.fallback(this.state.error);
        }
        return this.props.fallback;
      }
      return (
        <div style={{ padding: 32, textAlign: 'center', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>{'\u26A0\uFE0F'}</div>
          <p style={{ fontWeight: 700, fontSize: 16, color: T.text, marginBottom: 8 }}>Something went wrong</p>
          <p style={{ fontSize: 13, color: T.textMid, marginBottom: 20 }}>This part of Empirio ran into an error.</p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{ background: T.accent, border: 'none', borderRadius: 10, color: 'white',
              padding: '10px 24px', fontWeight: 700, fontSize: 14, cursor: 'pointer',
              fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function Card({ children, style, onClick, glow }) {
  return (
    <div onClick={onClick} style={{
      background: T.card, border: `1px solid ${T.border}`,
      borderRadius: 16, padding: 20,
      boxShadow: glow ? `0 0 24px ${glow}22` : "none",
      cursor: onClick ? "pointer" : "default",
      transition: "all .2s", ...style,
    }}
    onMouseOver={e => { if (onClick) { e.currentTarget.style.borderColor = T.borderHi; e.currentTarget.style.transform = "translateY(-2px)"; }}}
    onMouseOut={e => { if (onClick) { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.transform = "translateY(0)"; }}}
    >{children}</div>
  );
}

export function Badge({ label, color = T.accent }) {
  return (
    <span style={{
      background: color + "22", color, border: `1px solid ${color}44`,
      borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700,
      letterSpacing: "0.05em", textTransform: "uppercase",
    }}>{label}</span>
  );
}

export function ProgressRing({ value, max, size = 64, color = T.accent, label }) {
  const r = (size - 8) / 2, circ = 2 * Math.PI * r;
  const pct = Math.min(value / max, 1);
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={T.border} strokeWidth={5} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={5}
          strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
          strokeLinecap="round" style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      {label && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color }}>{label}</div>}
    </div>
  );
}

export function ProgressBar({ value, max, color = T.accent, height = 6 }) {
  return (
    <div style={{ background: T.border, borderRadius: 99, height, overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${Math.min((value/max)*100,100)}%`, background: color, borderRadius: 99, transition: "width .5s ease" }} />
    </div>
  );
}

export function DifficultyBadge({ level }) {
  const map = { 1: { label: "Bronze", color: "#CD7F32" }, 2: { label: "Silver", color: "#9CA3AF" }, 3: { label: "Gold", color: T.gold } };
  const d = map[level] || map[1];
  return <Badge label={d.label} color={d.color} />;
}

export function Confetti() {
  const pieces = Array.from({ length: 40 }, (_, i) => ({
    color: [T.accent, T.gold, T.green, T.purple, "#EC4899", "#06B6D4"][i % 6],
    left: Math.random() * 100, delay: Math.random() * 0.6,
    size: 6 + Math.random() * 8, dur: 1.5 + Math.random() * 1.2,
  }));
  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 9999, overflow: "hidden" }}>
      {pieces.map((p, i) => (
        <div key={i} style={{ position: "absolute", top: -20, left: `${p.left}%`, width: p.size, height: p.size, background: p.color, borderRadius: Math.random() > .5 ? "50%" : 2, animation: `confetti-fall ${p.dur}s ${p.delay}s ease-in forwards` }} />
      ))}
    </div>
  );
}

// ── RADAR CHART ────────────────────────────────────────────────
export function RadarChart({ data, size = 200 }) {
  if (!data || data.length < 3) return null;
  const safeData = data.map(d => ({ ...d, value: isNaN(d.value) ? 0 : Math.min(100, Math.max(0, d.value)) }));
  data = safeData;
  const cx = size / 2, cy = size / 2, r = size * 0.38;
  const n = data.length;
  const angleStep = (2 * Math.PI) / n;

  function getPoint(idx, radius) {
    const angle = idx * angleStep - Math.PI / 2;
    return { x: cx + radius * Math.cos(angle), y: cy + radius * Math.sin(angle) };
  }

  const gridLevels = [0.25, 0.5, 0.75, 1];
  const dataPoints = data.map((d, i) => getPoint(i, r * (d.value / 100)));
  const dataPath = dataPoints.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ") + "Z";

  return (
    <svg width={size + 160} height={size + 24} viewBox={`-80 -12 ${size + 160} ${size + 24}`} style={{ overflow: "visible", maxWidth: "100%" }}>
      {gridLevels.map(level => {
        const pts = Array.from({ length: n }, (_, i) => getPoint(i, r * level));
        const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ") + "Z";
        return <path key={level} d={path} fill="none" stroke={T.border} strokeWidth={1} />;
      })}
      {Array.from({ length: n }, (_, i) => {
        const pt = getPoint(i, r);
        return <line key={i} x1={cx} y1={cy} x2={pt.x} y2={pt.y} stroke={T.border} strokeWidth={1} />;
      })}
      <path d={dataPath} fill={T.accent + "33"} stroke={T.accent} strokeWidth={2} strokeLinejoin="round" />
      {dataPoints.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={4} fill={T.accent} />)}
      {data.map((d, i) => {
        const pt = getPoint(i, r * 1.22);
        return (
          <text key={i} x={pt.x} y={pt.y} textAnchor={pt.x > cx + 4 ? "start" : pt.x < cx - 4 ? "end" : "middle"} dominantBaseline="middle"
            style={{ fontSize: 9, fill: T.textMid, fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 600 }}>
            {d.label}
          </text>
        );
      })}
    </svg>
  );
}
